import ContentShell from "components/ContentShell";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button, Grid, Typography } from "@mui/material";
import configData from "config/config.json";
import { useSnackbar } from "hooks/useSnackbar";
import { makeStyles } from "@mui/styles";
import { SimpleTextInput } from "components/FormTextInput";
import { createGame, getGameInfo, updateGame } from "services/gameService";
import SimpleSwitch from "components/Switch";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: "24px",
    maxWidth: "1200px",
  },
  section: {
    marginBottom: "32px",
  },
  sectionTitle: {
    fontSize: "18px",
    fontWeight: 600,
    marginBottom: "16px",
    color: theme.palette.primary.main,
  },
  field: {
    marginBottom: "16px",
  },
  buttonContainer: {
    display: "flex",
    gap: "16px",
    justifyContent: "flex-end",
  },
  infoText: {
    fontSize: "12px",
    color: theme.palette.text.secondary,
    marginTop: "4px",
  },
}));

const _gameDetails = {
  gameName: "",
  gameAlias: "",
  launchPath: "",
  icon: "",
  homeURL: "",
  isActive: true,
};

const MasterGameEditor = () => {
  const classes = useStyles();
  const location = useLocation();
  const { showSnackbar } = useSnackbar();
  const id = location.pathname.split("/").slice(-1)[0];
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [gameDetails, setGameDetails] = useState(_gameDetails);
  const isEditMode = id !== "create";

  const cancel = () => navigate(configData.routes.masterGame.list);

  const validateForm = () => {
    if (!gameDetails.gameName?.trim()) {
      showSnackbar("Game Name is required", "error");
      return false;
    }
    if (!gameDetails.gameAlias?.trim() && !isEditMode) {
      showSnackbar("Game Alias is required", "error");
      return false;
    }

    return true;
  };

  const submit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      let response;

      if (isEditMode) {
        // Remove gameAlias from update payload as it's immutable
        const { gameAlias, _id, id, updatedAt, createdAt, defaultBet, maxBet, minBet, gameMode, ...updateData } = gameDetails as any;
        response = await updateGame(_id, updateData);
      } else {
        response = await createGame(gameDetails);
      }

      showSnackbar(response.message || "Game saved successfully", "success");
      setLoading(false);
      navigate(configData.routes.masterGame.list);
    } catch (error) {
      showSnackbar(error.message, "error");
      setLoading(false);
    }
  };

  const onChange = (e: any) => {
    const { name, value } = e?.target;
    setGameDetails((state) => ({
      ...state,
      [name]: name === "isActive" ? !state.isActive : value,
    }));
  };

  const fetchGameInfo = async () => {
    try {
      setLoading(true);
      const resp = await getGameInfo(id);
      setGameDetails(resp.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      showSnackbar(error.message, "error");
    }
  };

  useEffect(() => {
    if (isEditMode) fetchGameInfo();
  }, []);

  return (
    <ContentShell
      showBackButton
      onBackButtonClick={cancel}
      title={`${isEditMode ? "Edit" : "Create"} Master Game`}
      loading={loading}
      actions={
        <Box className={classes.buttonContainer}>
          <Button
            variant="contained"
            style={{ backgroundColor: "red", color: "white" }}
            onClick={cancel}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={submit} disabled={loading}>
            Save
          </Button>
        </Box>
      }
    >
      <Box className={classes.container}>
        {/* Basic Information Section */}
        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>
            Basic Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box className={classes.field}>
                <SimpleTextInput
                  label="Game Name *"
                  name="gameName"
                  value={gameDetails.gameName}
                  onChange={onChange}
                  placeholder="Enter game name"
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box className={classes.field}>
                <SimpleTextInput
                  label="Game Alias *"
                  name="gameAlias"
                  value={gameDetails.gameAlias}
                  onChange={onChange}
                  placeholder="Enter game alias"
                  disabled={isEditMode}
                />
                {isEditMode && (
                  <Typography className={classes.infoText}>
                    Game Alias cannot be changed after creation
                  </Typography>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box className={classes.field}>
                <SimpleTextInput
                  label="Launch Path"
                  name="launchPath"
                  value={gameDetails.launchPath}
                  onChange={onChange}
                  placeholder="Enter launch path (optional)"
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box className={classes.field}>
                <SimpleTextInput
                  label="Home URL"
                  name="homeURL"
                  value={gameDetails.homeURL}
                  onChange={onChange}
                  placeholder="Enter home URL (optional)"
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box className={classes.field}>
                <SimpleTextInput
                  label="Icon URL"
                  name="icon"
                  value={gameDetails.icon}
                  onChange={onChange}
                  placeholder="Enter icon URL (optional)"
                />
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Status Section */}
        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>Status</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box className={classes.field}>
                <SimpleSwitch
                  label="Active"
                  name="isActive"
                  value={gameDetails.isActive}
                  onChange={onChange}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </ContentShell>
  );
};

export default MasterGameEditor;
